// ============================================================
// ENGINE — ROOM BACKDROP (Gilded Obsidian)
// ============================================================
// "So it's not just black." — caP'n, July 2026
// "Mythic will blow you away. I want to throw everything at it."
//                                          — caP'n, July 9 2026
//
// The letterbox space around every walkable room is a COSMETIC
// SURFACE: unlockable/purchasable backdrops rendered behind the
// room map, confined to THE STAGE BAND (the right chrome rail).
// Pure CSS/SVG — live today, CDN art drops in later as a recipe
// swap, exactly like HoloFoil tiers.
//
// v3 — THE GREAT RE-TIERING (July 9, 2026):
//   • Everything that existed before today is FABLE. caP'n's law:
//     the old "mythic" Molten Sigil was placeholder-grade — base
//     level. Rarity is display-only (never written to Firebase),
//     so demotion is cosmetic: owners keep everything.
//   • FABLE      — one mood, one motion. The everyday drop.
//   • LEGENDARY  — the band gets BORDERS: light runners racing the
//     edges, flicker, weather. 3–4 bespoke layers.
//   • MYTHIC     — everything at once. Parallax, counter-rotation,
//     SVG turbulence, ripples, flares, edge fire. 6+ layers, each
//     with a motion signature nothing else in the catalog has.
//   • FAMILIES   — every backdrop belongs to a classification
//     (VOID / EMBER / TOME / CREST / BEING / CHAOS / STORM / SOL)
//     shown as a chip in the Atelier. Display-only, like rarity.
//
// FOUR EXPORTS (unchanged shape):
//   BACKDROPS / useEquippedBackdrop / <RoomBackdrop/> / <BackdropAtelier/>
//   plus the band + stage helpers (getStageBand, BackdropUnderlay…).
//
// LAWS HONORED:
//   • Purchases via progression.spendCoins (atomic, ◕-only).
//   • Unlocks APPEND-ONLY at profiles/{uid}/roomBackdrops/unlocked.
//   • GRANT-BEFORE-FLAG — a failed equip never burns a purchase.
//   • Equip anywhere, appear everywhere.
//   • Unique motion signature per backdrop — never a recolor.
//   • Keyframes injected imperatively, id-guarded (Material Law 6).
//   • SVG filter IDs via useId() — never shared (HoloFoil law).
//   • Every new item costs 1◆ (going-forward rule, July 8).
//
// CUT-OFF FIX (July 9): getStageBand now measures
// document.documentElement.clientWidth/Height instead of
// window.innerWidth/Height. innerWidth INCLUDES the scrollbar,
// so whenever a page scrollbar appeared the band's right edge
// slid underneath it and the art was visibly cropped.
// ============================================================

import { useRail } from "./railDock"; // the band folds into the MENU pill — art and all
import { useState, useEffect, useId } from "react";
import { GM } from "./theme";
import { ref, onValue } from "firebase/database";
import { db } from "./firebase";
import { unlockBackdrop, equipBackdrop } from "./progression";

// ── Keyframes — imperative, id-guarded (v3 id; evicts v1/v2) ──
function ensureBackdropKeyframes() {
  if (typeof document === "undefined") return;
  const old = document.getElementById("rb-keyframes");
  if (old) old.remove();
  if (document.getElementById("rb-keyframes-v3")) return;
  const el = document.createElement("style");
  el.id = "rb-keyframes-v3";
  el.textContent = `
    @keyframes rbEmberBreathe { 0%,100%{opacity:0.55} 50%{opacity:0.95} }
    @keyframes rbDrift { from{background-position:0% 0%} to{background-position:200% 0%} }
    @keyframes rbCoil { 0%{background-position:50% 0%} 50%{background-position:50% 100%} 100%{background-position:50% 0%} }
    @keyframes rbRain { from{background-position:0 -140px} to{background-position:0 0} }
    @keyframes rbSpin { from{transform:rotate(0deg) scale(1.6)} to{transform:rotate(360deg) scale(1.6)} }
    @keyframes rbPrism { 0%,100%{filter:hue-rotate(0deg);opacity:0.5} 50%{filter:hue-rotate(60deg);opacity:0.85} }
    @keyframes rbSigilSpin { from{transform:rotate(360deg)} to{transform:rotate(0deg)} }
    @keyframes rbSigilPulse { 0%,100%{opacity:0.35;transform:scale(1)} 50%{opacity:0.8;transform:scale(1.1)} }

    /* v3 — the legendary/mythic vocabulary */
    @keyframes rbRunner { from{background-position:0 240px} to{background-position:0 0} }
    @keyframes rbRunnerX { from{background-position:240px 0} to{background-position:0 0} }
    @keyframes rbRise {
      from { background-position: 0 0, 33px 116px; }
      to   { background-position: 0 -640px, 33px -344px; }
    }
    @keyframes rbFlicker {
      0%,100%{opacity:0.55} 8%{opacity:0.9} 16%{opacity:0.5} 27%{opacity:0.85}
      38%{opacity:0.6} 52%{opacity:1} 64%{opacity:0.55} 79%{opacity:0.9} 90%{opacity:0.65}
    }
    @keyframes rbFlash {
      0%,83%,100%{opacity:0}
      84%{opacity:0.85} 85%{opacity:0.15} 86.5%{opacity:0.65} 88%{opacity:0}
      93%{opacity:0.4} 94%{opacity:0}
    }
    @keyframes rbHueDrift { from{filter:hue-rotate(0deg)} to{filter:hue-rotate(360deg)} }
    @keyframes rbRipple {
      0%{transform:scale(0.5);opacity:0.55}
      70%{opacity:0.15}
      100%{transform:scale(1.6);opacity:0}
    }
    @keyframes rbSpinFwd { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
    @keyframes rbCorona {
      0%,100%{opacity:0.5;transform:scale(1)}
      50%{opacity:0.95;transform:scale(1.12)}
    }
    /* THE GRIDDLE, at room scale. One diagonal pass that PRESSES
       through the middle — the same law as HoloFoil's hf-griddle and
       SourceSigil's ss-griddle, so the backdrop, the foil and the mark
       a Minerium One card wears are all one gesture. */
    @keyframes rbSear {
      0%   { transform: translateX(-120%) skewX(-13deg) scaleX(1);   opacity: 0; }
      14%  { opacity: 1; }
      44%  { transform: translateX(-6%)   skewX(-13deg) scaleX(1.1); opacity: 1; }
      58%  { transform: translateX(6%)    skewX(-13deg) scaleX(1.16);opacity: 1; }
      86%  { opacity: 1; }
      100% { transform: translateX(120%)  skewX(-13deg) scaleX(1);   opacity: 0; }
    }
    @keyframes rbScripture { from{background-position:0 480px} to{background-position:0 0} }

    @media (prefers-reduced-motion: reduce) {
      [data-rb-motion] { animation: none !important; }
    }
  `;
  document.head.appendChild(el);
}
ensureBackdropKeyframes();

// ── Reward-tier chrome — THE THREE CLASSES (caP'n, July 8) ────
// FABLE (common) / LEGENDARY (mid) / MYTHIC (rarest — signatures
// are ALWAYS mythic). `rarity` is display-only, never written to
// Firebase, so re-bucketing is cosmetic — no schema break. Legacy
// v1.1 keys kept as aliases so old strings still resolve.
export const BACKDROP_RARITY = {
  fable:     { label: "FABLE",     color: "#b3a68c" },
  legendary: { label: "LEGENDARY", color: "#e8b84b" },
  mythic:    { label: "MYTHIC",    color: "#f43f5e" },
  // Legacy aliases → folded into the three (back-compat only)
  common:    { label: "FABLE",     color: "#b3a68c" },
  uncommon:  { label: "FABLE",     color: "#b3a68c" },
  rare:      { label: "LEGENDARY", color: "#e8b84b" },
  epic:      { label: "LEGENDARY", color: "#e8b84b" },
};

// ── FAMILIES — the classification chips (display-only) ────────
// Every backdrop carries exactly one family. Never written to
// Firebase; purely Atelier chrome + future filter fodder.
export const BACKDROP_FAMILIES = {
  void:  { label: "VOID",  color: "#8b7bb8" },
  ember: { label: "EMBER", color: "#f97316" },
  tome:  { label: "TOME",  color: "#a3e635" },
  crest: { label: "CREST", color: "#e8b84b" },
  being: { label: "BEING", color: "#4ade80" },
  chaos: { label: "CHAOS", color: "#d946ef" },
  storm: { label: "STORM", color: "#38bdf8" },
  sol:   { label: "SOL",   color: "#fbbf24" },
};

// ── LAYER HELPERS (the overseer pattern for effects) ──────────
// Small factories so legendaries/mythics compose bespoke stacks
// without copy-paste drift. Every consumer still gets a UNIQUE
// combination — helpers are vocabulary, not recolors.

// A light runner racing along one border of the band.
// NOTE: the band's LEFT edge is feather-masked toward the map on
// desktop, so left runners melt there by design (they show in
// full on portrait, where there's no mask).
function edgeRunner(key, { side = "right", color, duration = 6, width = 3, opacity = 0.9 }) {
  const vertical = side === "left" || side === "right";
  const geom = vertical
    ? { top: 0, bottom: 0, width, [side]: 0 }
    : { left: 0, right: 0, height: width, [side]: 0 };
  return (
    <div key={key} data-rb-motion="" style={{
      position: "absolute", pointerEvents: "none", opacity, ...geom,
      background: vertical
        ? `repeating-linear-gradient(180deg, transparent 0px, ${color} 26px, transparent 60px)`
        : `repeating-linear-gradient(90deg, transparent 0px, ${color} 26px, transparent 60px)`,
      backgroundSize: vertical ? "100% 240px" : "240px 100%",
      animation: `${vertical ? "rbRunner" : "rbRunnerX"} ${duration}s linear infinite`,
      boxShadow: `0 0 9px ${color}`,
    }} />
  );
}

// Specks drifting upward — embers, souls, stars, sparks.
// Two offset dot layers at different tile sizes = organic parallax.
// Layer order must match rbRise's two background-position tracks.
function riseSpecks(key, { color, duration = 14, opacity = 0.7, size = 2, blur = 0 }) {
  const s2 = Math.max(1, size - 1);
  return (
    <div key={key} data-rb-motion="" style={{
      position: "absolute", inset: 0, opacity, pointerEvents: "none",
      backgroundImage:
        `radial-gradient(circle, ${color} 0px, ${color} ${size}px, transparent ${size + 1.5}px),` +
        `radial-gradient(circle, ${color} 0px, ${color} ${s2}px, transparent ${s2 + 1.5}px)`,
      backgroundSize: "97px 160px, 61px 215px",
      animation: `rbRise ${duration}s linear infinite`,
      filter: blur ? `blur(${blur}px)` : undefined,
    }} />
  );
}

// SVG turbulence miasma — real fractal smoke (HoloFoil law: CSS
// gradients are too smooth for smoke). Component, not factory,
// because filter IDs must be unique per instance (useId).
function MiasmaLayer({ color = "#4ade80", opacity = 0.22, dur = "26s" }) {
  const rawId = useId();
  const fid = `rb-miasma-${rawId.replace(/[^a-zA-Z0-9_-]/g, "")}`;
  return (
    <svg data-rb-motion="" aria-hidden="true"
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", opacity, pointerEvents: "none" }}
      preserveAspectRatio="none">
      <filter id={fid}>
        <feTurbulence type="fractalNoise" baseFrequency="0.006 0.014" numOctaves="3" seed="7">
          <animate attributeName="baseFrequency"
            values="0.006 0.014;0.009 0.018;0.006 0.014" dur={dur} repeatCount="indefinite" />
        </feTurbulence>
        <feColorMatrix type="matrix"
          values="0 0 0 0 0.29  0 0 0 0 0.87  0 0 0 0 0.50  0 0 0 0.9 0" />
        <feComposite operator="in" in2="SourceGraphic" />
      </filter>
      <rect x="0" y="0" width="100%" height="100%" fill={color} filter={`url(#${fid})`} />
    </svg>
  );
}

// ── THE CATALOG ───────────────────────────────────────────────
// Unique motion signature per backdrop — never a recolor.
export const DEFAULT_BACKDROP_ID = "obsidian";

export const BACKDROPS = [

  // ════════════════ FABLE — one mood, one motion ══════════════
  // (The entire pre-July-9 catalog lives here now. caP'n's call:
  //  yesterday's "mythic" is today's base level. Owners keep all.)
  {
    id: "obsidian", name: "Gilded Obsidian", rarity: "fable", family: "void", cost: 0,
    lore: "The void as the Minerium left it. Free, forever.",
    render: () => (
      <div key="base" style={{
        position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at 50% 40%, #0b0718 0%, #070410 55%, #030208 100%)",
      }} />
    ),
  },
  {
    id: "emberline", name: "Emberline", rarity: "fable", family: "ember", cost: 1,
    lore: "Willie's forge never fully sleeps. Neither does this.",
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0,
        background: "linear-gradient(180deg, #070410 0%, #120708 60%, #1c0a06 100%)" }} />,
      <div key="b" data-rb-motion="" style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: "40%",
        background: "radial-gradient(ellipse at 50% 100%, rgba(249,115,22,0.16), transparent 70%)",
        animation: "rbEmberBreathe 4s ease-in-out infinite" }} />,
    ],
  },
  {
    id: "deep_vellum", name: "Deep Vellum", rarity: "fable", family: "tome", cost: 1,
    lore: "Moonlight through a page nobody finished writing.",
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0,
        background: "linear-gradient(165deg, #0c0a14 0%, #131022 55%, #0a0812 100%)" }} />,
      <div key="b" data-rb-motion="" style={{ position: "absolute", inset: 0, opacity: 0.5,
        background: "linear-gradient(100deg, transparent 30%, rgba(236,226,204,0.05) 50%, transparent 70%)",
        backgroundSize: "200% 100%", animation: "rbDrift 14s linear infinite" }} />,
    ],
  },
  {
    id: "amethyst_drift", name: "Amethyst Drift", rarity: "fable", family: "void", cost: 1,
    lore: "The Crystal dreamed once. This is what leaked out.",
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at 30% 20%, #170b2e 0%, #0a0616 55%, #050310 100%)" }} />,
      <div key="b" data-rb-motion="" style={{ position: "absolute", inset: 0,
        background: "linear-gradient(115deg, transparent 20%, rgba(147,51,234,0.12) 45%, rgba(109,40,217,0.08) 55%, transparent 80%)",
        backgroundSize: "200% 100%", animation: "rbDrift 22s linear infinite" }} />,
    ],
  },
  {
    id: "serpents_coil", name: "Serpent's Coil", rarity: "fable", family: "being", cost: 1,
    lore: "Vyen swears her cousins had nothing to do with this.",
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0,
        background: "linear-gradient(180deg, #050c08 0%, #071510 50%, #04100a 100%)" }} />,
      <div key="b" data-rb-motion="" style={{ position: "absolute", inset: 0, opacity: 0.7,
        background: "repeating-linear-gradient(0deg, transparent 0px, rgba(74,222,128,0.05) 40px, transparent 80px)",
        backgroundSize: "100% 300%", animation: "rbCoil 16s ease-in-out infinite" }} />,
    ],
  },
  {
    id: "crest_rain", name: "Crest Rain", rarity: "fable", family: "crest", cost: 1,
    // ⚠ THE SEALED NAME CAME OUT OF THIS BLURB (2026-08-20). It read
    // "Somewhere above, X'Aol is losing count on purpose." A backdrop's
    // lore line is a character mention, not a card's printed label, so
    // the mechanic exemption does not cover it — PRIME §2.8 allows the
    // name exactly once, in the Rulebook. The joke was the counting and
    // the joke survives; unnamed, it is also better, because now you do
    // not know who is up there.
    lore: "Somewhere above, someone is losing count on purpose.",
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at 50% 0%, #14100a 0%, #0a0810 60%, #050408 100%)" }} />,
      <div key="b" data-rb-motion="" style={{ position: "absolute", inset: 0, opacity: 0.5,
        background: "repeating-linear-gradient(180deg, rgba(200,150,60,0.10) 0px, transparent 8px, transparent 130px, rgba(232,184,75,0.07) 140px)",
        backgroundSize: "100% 140px", animation: "rbRain 6s linear infinite" }} />,
    ],
  },
  {
    id: "jynxis_laughter", name: "J'ynxi's Laughter", rarity: "fable", family: "chaos", cost: 1,
    lore: "Ha. Ha. Ha. (It never fully stops spinning. She won't let it.)",
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0, background: "#060310" }} />,
      <div key="b" data-rb-motion="" style={{ position: "absolute", inset: "-30%",
        background: "conic-gradient(from 0deg at 50% 50%, transparent 0deg, rgba(217,70,239,0.10) 70deg, transparent 140deg, rgba(34,211,238,0.09) 220deg, transparent 300deg, rgba(217,70,239,0.06) 340deg, transparent 360deg)",
        animation: "rbSpin 60s linear infinite" }} />,
    ],
  },
  {
    // Was the "signature test piece" mythic. Served its purpose —
    // caP'n's verdict July 9: placeholder-grade, base level. Demoted.
    id: "molten_sigil", name: "Molten Sigil", rarity: "fable", family: "ember", cost: 1,
    lore: "Willie stamped the void itself. Warranty voided, obviously.",
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at 50% 55%, #1a0708 0%, #0e040a 55%, #050208 100%)" }} />,
      <div key="b" data-rb-motion="" style={{ position: "absolute", inset: "-25%",
        background: "conic-gradient(from 0deg at 50% 50%, transparent 0deg, rgba(244,63,94,0.14) 40deg, transparent 90deg, rgba(232,184,75,0.12) 150deg, transparent 200deg, rgba(244,63,94,0.10) 260deg, transparent 320deg)",
        animation: "rbSigilSpin 48s linear infinite" }} />,
      <div key="c" data-rb-motion="" style={{ position: "absolute", inset: 0,
        background: "radial-gradient(circle at 50% 55%, transparent 30%, rgba(244,63,94,0.10) 42%, transparent 52%, rgba(232,184,75,0.08) 60%, transparent 70%)",
        animation: "rbSigilPulse 5s ease-in-out infinite" }} />,
    ],
  },
  {
    id: "shatterlight", name: "Shatterlight", rarity: "fable", family: "sol", cost: 1,
    lore: "What the Shatterfield sees when it closes its eyes.",
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0,
        background: "linear-gradient(160deg, #0a0714 0%, #100a1c 45%, #070510 100%)" }} />,
      <div key="b" data-rb-motion="" style={{ position: "absolute", inset: 0,
        background: "conic-gradient(from 200deg at 30% 70%, transparent, rgba(56,189,248,0.10), transparent 30%), conic-gradient(from 20deg at 70% 30%, transparent, rgba(244,63,94,0.08), transparent 25%), conic-gradient(from 120deg at 55% 55%, transparent, rgba(232,184,75,0.08), transparent 20%)",
        animation: "rbPrism 9s ease-in-out infinite" }} />,
    ],
  },

  // ══════════ LEGENDARY — the borders come alive ══════════════
  {
    id: "gilded_procession", name: "Gilded Procession", rarity: "legendary", family: "crest", cost: 1,
    lore: "The Minerium's honor guard marches the edge of the void. Forever.",
    // Signature: gold runners on THREE borders (right, top, bottom)
    // moving in procession over a slow vellum sheen. No other
    // backdrop animates its borders in convoy.
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at 50% 30%, #141008 0%, #0c0906 55%, #050403 100%)" }} />,
      <div key="b" data-rb-motion="" style={{ position: "absolute", inset: 0, opacity: 0.45,
        background: "linear-gradient(100deg, transparent 25%, rgba(232,184,75,0.09) 50%, transparent 75%)",
        backgroundSize: "200% 100%", animation: "rbDrift 18s linear infinite" }} />,
      edgeRunner("r1", { side: "right",  color: "rgba(232,184,75,0.85)", duration: 5, width: 3 }),
      edgeRunner("r2", { side: "top",    color: "rgba(232,184,75,0.55)", duration: 7, width: 2 }),
      edgeRunner("r3", { side: "bottom", color: "rgba(232,184,75,0.55)", duration: 7, width: 2 }),
    ],
  },
  {
    id: "wyrmfire_gate", name: "Wyrmfire Gate", rarity: "legendary", family: "ember", cost: 1,
    lore: "Something on the other side keeps the pilot light on.",
    // Signature: flickering fire-columns hugging both edges + real
    // rising embers. The only backdrop whose borders BURN.
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0,
        background: "linear-gradient(180deg, #0a0405 0%, #170807 55%, #241006 100%)" }} />,
      <div key="b" data-rb-motion="" style={{ position: "absolute", top: 0, bottom: 0, right: 0, width: "26%",
        background: "linear-gradient(270deg, rgba(249,115,22,0.22), transparent)",
        animation: "rbFlicker 3.2s ease-in-out infinite" }} />,
      <div key="c" data-rb-motion="" style={{ position: "absolute", top: 0, bottom: 0, left: 0, width: "26%",
        background: "linear-gradient(90deg, rgba(244,63,94,0.16), transparent)",
        animation: "rbFlicker 4.1s ease-in-out infinite reverse" }} />,
      riseSpecks("d", { color: "rgba(251,146,60,0.75)", duration: 9, opacity: 0.8, size: 2 }),
      <div key="e" data-rb-motion="" style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: "30%",
        background: "radial-gradient(ellipse at 50% 100%, rgba(249,115,22,0.25), transparent 75%)",
        animation: "rbEmberBreathe 3s ease-in-out infinite" }} />,
    ],
  },
  {
    id: "grimoire_whisper", name: "Grimoire Whisper", rarity: "legendary", family: "tome", cost: 1,
    lore: "Willee shelved this one spine-inward. It reads itself anyway.",
    // Signature: scrolling scripture — faint ruled ink lines rising
    // like turning pages — under a slow forward-spinning rune wheel.
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0,
        background: "linear-gradient(170deg, #0b0d08 0%, #10140a 50%, #070905 100%)" }} />,
      <div key="b" data-rb-motion="" style={{ position: "absolute", inset: 0, opacity: 0.5,
        background: "repeating-linear-gradient(180deg, transparent 0px, transparent 26px, rgba(163,230,53,0.07) 27px, transparent 29px, transparent 47px, rgba(236,226,204,0.05) 48px)",
        backgroundSize: "100% 480px", animation: "rbScripture 26s linear infinite" }} />,
      <div key="c" data-rb-motion="" style={{ position: "absolute", inset: "-20%",
        background: "conic-gradient(from 90deg at 50% 50%, transparent 0deg, rgba(163,230,53,0.07) 30deg, transparent 60deg, rgba(163,230,53,0.05) 180deg, transparent 210deg, rgba(236,226,204,0.05) 300deg, transparent 330deg)",
        animation: "rbSpinFwd 90s linear infinite" }} />,
      edgeRunner("d", { side: "right", color: "rgba(163,230,53,0.45)", duration: 11, width: 2, opacity: 0.7 }),
    ],
  },
  {
    id: "stormbind", name: "Stormbind", rarity: "legendary", family: "storm", cost: 1,
    lore: "Bottled off the Pier on a night Pierre refuses to discuss.",
    // Signature: the only backdrop with WEATHER — driving rain plus
    // irregular lightning strikes that light the whole band.
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0,
        background: "linear-gradient(180deg, #060a12 0%, #0a1220 55%, #050810 100%)" }} />,
      <div key="b" data-rb-motion="" style={{ position: "absolute", inset: 0, opacity: 0.45,
        background: "repeating-linear-gradient(170deg, rgba(56,189,248,0.10) 0px, transparent 6px, transparent 90px, rgba(148,197,255,0.08) 96px)",
        backgroundSize: "100% 140px", animation: "rbRain 2.4s linear infinite" }} />,
      <div key="c" data-rb-motion="" style={{ position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at 60% 20%, rgba(190,225,255,0.5), transparent 60%)",
        animation: "rbFlash 7s linear infinite" }} />,
      <div key="d" data-rb-motion="" style={{ position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at 30% 55%, rgba(148,197,255,0.4), transparent 55%)",
        animation: "rbFlash 11s linear infinite reverse" }} />,
      edgeRunner("e", { side: "right", color: "rgba(56,189,248,0.6)", duration: 4, width: 2 }),
    ],
  },

  // ═══════════ MYTHIC — throw everything at it ════════════════
  {
    // ⭐ THE PUPIL'S PRIZE, 2026-08-22. caP'n: *"the middle pupil will
    // give you a Mythical SHV Backdrop and Coin and Lux/Grim aura."*
    // Earned only at ASS-50 — the deepest door in Minerium One, behind
    // either all six Sol Kings of the Eyeland or all four eggs of the
    // Crimson Dais. `cost: 0` is deliberate: it is not on the shelf and
    // it never will be. Do the walk or do without.
    //
    // Signature: the Crimson Jewel as a ROOM. Held-breath red at the
    // centre, black obsidian at the walls, a slow ring of Antdroid
    // rank-light in procession — and once per cycle THE GRIDDLE
    // crosses the whole room, white-gold to umber, the same gesture
    // his foil and his sigil make. Nothing else in the catalog sears.
    id: "shv_crimson", name: "The Crimson Jewel", rarity: "mythic", family: "sol", cost: 0,
    lore: "A kingdom the size of a held breath, and he is not in it any more.",
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at 50% 48%, #3a0410 0%, #1a040c 46%, #0a0206 78%, #030102 100%)" }} />,
      <div key="b" data-rb-motion="" style={{ position: "absolute", inset: 0,
        background: "radial-gradient(circle at 50% 48%, rgba(255,60,90,0.20) 0%, rgba(193,122,46,0.08) 22%, transparent 44%)",
        animation: "rbCorona 7s ease-in-out infinite" }} />,
      <div key="c" data-rb-motion="" style={{ position: "absolute", inset: "-25%",
        background: "conic-gradient(from 0deg at 50% 48%, transparent 0deg, rgba(193,122,46,0.13) 34deg, transparent 78deg, rgba(255,60,90,0.10) 170deg, transparent 220deg, rgba(253,246,227,0.07) 300deg, transparent 348deg)",
        animation: "rbSpin 64s linear infinite" }} />,
      riseSpecks("d", { color: "rgba(253,246,227,0.5)", duration: 30, opacity: 0.4, size: 1 }),
      riseSpecks("e", { color: "rgba(255,90,60,0.45)", duration: 17, opacity: 0.36, size: 2, blur: 0.6 }),
      // THE SEAR. Held to 0.14 on purpose — a room is looked at for an
      // hour, not for the three seconds a card face gets, and anything
      // that would read correctly on a card is unbearable on a wall.
      <div key="f" data-rb-motion="" style={{ position: "absolute", inset: "-10% -40%",
        background: "linear-gradient(100deg, transparent 0%, rgba(253,246,227,0.55) 34%, rgba(193,122,46,0.45) 52%, rgba(58,34,16,0.4) 68%, transparent 100%)",
        mixBlendMode: "overlay", filter: "blur(14px)", opacity: 0.14,
        animation: "rbSear 13s ease-in-out infinite" }} />,
      edgeRunner("g1", { side: "left",   color: "rgba(193,122,46,0.7)", duration: 11, width: 2 }),
      edgeRunner("g2", { side: "right",  color: "rgba(255,60,90,0.55)", duration: 13, width: 2 }),
      edgeRunner("g3", { side: "bottom", color: "rgba(253,246,227,0.4)", duration: 16, width: 2 }),
    ],
  },
  {
    id: "emperiums_eye", name: "The Minerium's Eye", rarity: "mythic", family: "void", cost: 1,
    lore: "It opened once, at the founding. It has not blinked since.",
    // Signature: PARALLAX starfield (two drift speeds) beneath
    // counter-rotating iris rings, a breathing golden pupil, and
    // a full border of gold light in procession. Watched, always.
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at 50% 45%, #100a20 0%, #080512 55%, #030208 100%)" }} />,
      riseSpecks("b", { color: "rgba(236,226,204,0.55)", duration: 46, opacity: 0.5, size: 1 }),
      riseSpecks("c", { color: "rgba(147,51,234,0.45)", duration: 24, opacity: 0.4, size: 2, blur: 0.5 }),
      <div key="d" data-rb-motion="" style={{ position: "absolute", inset: "-25%",
        background: "conic-gradient(from 0deg at 50% 45%, transparent 0deg, rgba(232,184,75,0.12) 30deg, transparent 70deg, rgba(147,51,234,0.10) 140deg, transparent 190deg, rgba(232,184,75,0.09) 260deg, transparent 320deg)",
        animation: "rbSpin 55s linear infinite" }} />,
      <div key="e" data-rb-motion="" style={{ position: "absolute", inset: "-25%",
        background: "conic-gradient(from 180deg at 50% 45%, transparent 0deg, rgba(109,40,217,0.10) 45deg, transparent 100deg, rgba(232,184,75,0.07) 220deg, transparent 280deg)",
        animation: "rbSigilSpin 34s linear infinite" }} />,
      <div key="f" data-rb-motion="" style={{ position: "absolute", inset: 0,
        background: "radial-gradient(circle at 50% 45%, rgba(232,184,75,0.20) 0%, rgba(232,184,75,0.08) 9%, transparent 16%)",
        animation: "rbCorona 6s ease-in-out infinite" }} />,
      edgeRunner("g1", { side: "right",  color: "rgba(232,184,75,0.8)", duration: 6, width: 3 }),
      edgeRunner("g2", { side: "left",   color: "rgba(147,51,234,0.6)", duration: 8, width: 3 }),
      edgeRunner("g3", { side: "top",    color: "rgba(232,184,75,0.5)", duration: 9, width: 2 }),
      edgeRunner("g4", { side: "bottom", color: "rgba(232,184,75,0.5)", duration: 9, width: 2 }),
    ],
  },
  {
    id: "jynxi_unsealed", name: "J'ynxi, Unsealed", rarity: "mythic", family: "chaos", cost: 1,
    lore: "V'ael's one failure, framed and hung where everyone can see it. Rude.",
    // Signature: the whole band slowly cycles through EVERY hue
    // while dual conics counter-spin at mismatched speeds, sparks
    // race upward, and laughter-rings ripple out of nowhere. The
    // only backdrop that is never the same color twice.
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0, background: "#070312" }} />,
      <div key="hue" data-rb-motion="" style={{ position: "absolute", inset: 0,
        animation: "rbHueDrift 30s linear infinite" }}>
        <div data-rb-motion="" style={{ position: "absolute", inset: "-30%",
          background: "conic-gradient(from 0deg at 50% 50%, transparent 0deg, rgba(217,70,239,0.16) 60deg, transparent 130deg, rgba(34,211,238,0.14) 210deg, transparent 290deg)",
          animation: "rbSpin 26s linear infinite" }} />
        <div data-rb-motion="" style={{ position: "absolute", inset: "-30%",
          background: "conic-gradient(from 90deg at 50% 50%, transparent 0deg, rgba(34,211,238,0.12) 50deg, transparent 120deg, rgba(217,70,239,0.10) 230deg, transparent 300deg)",
          animation: "rbSigilSpin 17s linear infinite" }} />
      </div>,
      riseSpecks("d", { color: "rgba(217,70,239,0.8)", duration: 7, opacity: 0.75, size: 2 }),
      <div key="e" data-rb-motion="" style={{ position: "absolute", inset: 0,
        background: "radial-gradient(circle at 50% 50%, transparent 34%, rgba(217,70,239,0.30) 37%, transparent 41%)",
        animation: "rbRipple 5s ease-out infinite" }} />,
      <div key="f" data-rb-motion="" style={{ position: "absolute", inset: 0,
        background: "radial-gradient(circle at 50% 50%, transparent 34%, rgba(34,211,238,0.26) 37%, transparent 41%)",
        animation: "rbRipple 5s ease-out infinite", animationDelay: "2.5s" }} />,
      edgeRunner("g1", { side: "right", color: "rgba(217,70,239,0.75)", duration: 3.5, width: 3 }),
      edgeRunner("g2", { side: "left",  color: "rgba(34,211,238,0.7)",  duration: 4.5, width: 3 }),
    ],
  },
  {
    id: "necropolis_bloom", name: "Necropolis Bloom", rarity: "mythic", family: "tome", cost: 1,
    lore: "The fallen don't rest here. They garden.",
    // Signature: REAL fractal miasma (SVG turbulence — nothing else
    // in the catalog uses it) crawling beneath rising blurred soul-
    // wisps, a slow rune wheel, and ectoplasm breathing at the edges.
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0,
        background: "linear-gradient(180deg, #060a08 0%, #0a1410 45%, #120a18 100%)" }} />,
      <MiasmaLayer key="b" color="#4ade80" opacity={0.20} dur="26s" />,
      riseSpecks("c", { color: "rgba(163,230,53,0.65)", duration: 18, opacity: 0.7, size: 3, blur: 1.5 }),
      <div key="d" data-rb-motion="" style={{ position: "absolute", inset: "-15%",
        background: "conic-gradient(from 0deg at 50% 60%, transparent 0deg, rgba(74,222,128,0.08) 20deg, transparent 40deg, rgba(147,51,234,0.07) 110deg, transparent 130deg, rgba(74,222,128,0.06) 200deg, transparent 220deg, rgba(147,51,234,0.05) 300deg, transparent 320deg)",
        animation: "rbSpinFwd 70s linear infinite" }} />,
      <div key="e" data-rb-motion="" style={{ position: "absolute", inset: 0,
        background: "radial-gradient(circle at 50% 62%, rgba(74,222,128,0.14) 0%, transparent 24%)",
        animation: "rbSigilPulse 7s ease-in-out infinite" }} />,
      <div key="f" data-rb-motion="" style={{ position: "absolute", top: 0, bottom: 0, right: 0, width: "18%",
        background: "linear-gradient(270deg, rgba(74,222,128,0.16), transparent)",
        animation: "rbFlicker 5.3s ease-in-out infinite" }} />,
      edgeRunner("g", { side: "right", color: "rgba(74,222,128,0.55)", duration: 8, width: 2, opacity: 0.75 }),
    ],
  },
  {
    id: "solrend_eternal", name: "Solrend Eternal", rarity: "mythic", family: "sol", cost: 1,
    lore: "Every duel ever fought, still burning in one sky.",
    // Signature: a total ECLIPSE — black disc, breathing corona,
    // rotating light-rays sweeping the band, golden ember-fall,
    // and solar flares that erupt on their own schedule.
    render: () => [
      <div key="a" style={{ position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at 50% 38%, #170e05 0%, #0c0704 50%, #050302 100%)" }} />,
      <div key="b" data-rb-motion="" style={{ position: "absolute", inset: "-25%",
        background: "conic-gradient(from 0deg at 50% 38%, transparent 0deg, rgba(251,191,36,0.12) 14deg, transparent 28deg, rgba(251,191,36,0.09) 100deg, transparent 114deg, rgba(249,115,22,0.10) 190deg, transparent 204deg, rgba(251,191,36,0.08) 290deg, transparent 304deg)",
        animation: "rbSpin 40s linear infinite" }} />,
      <div key="c" data-rb-motion="" style={{ position: "absolute", inset: 0,
        background: "radial-gradient(circle at 50% 38%, transparent 8%, rgba(251,191,36,0.35) 10.5%, rgba(249,115,22,0.14) 15%, transparent 23%)",
        animation: "rbCorona 5s ease-in-out infinite" }} />,
      <div key="d" style={{ position: "absolute", inset: 0,
        background: "radial-gradient(circle at 50% 38%, #030201 0%, #030201 8%, transparent 8.6%)" }} />,
      riseSpecks("e", { color: "rgba(251,191,36,0.6)", duration: 20, opacity: 0.55, size: 1 }),
      <div key="f" data-rb-motion="" style={{ position: "absolute", inset: 0,
        background: "radial-gradient(ellipse at 50% 32%, rgba(251,191,36,0.45), transparent 45%)",
        animation: "rbFlash 13s linear infinite" }} />,
      edgeRunner("g1", { side: "right",  color: "rgba(251,191,36,0.8)", duration: 5, width: 3 }),
      edgeRunner("g2", { side: "top",    color: "rgba(249,115,22,0.55)", duration: 6.5, width: 2 }),
      edgeRunner("g3", { side: "bottom", color: "rgba(249,115,22,0.55)", duration: 6.5, width: 2 }),
    ],
  },
];

export const getBackdrop = (id) =>
  BACKDROPS.find(b => b.id === id) || BACKDROPS.find(b => b.id === DEFAULT_BACKDROP_ID);

// ── LIVE EQUIPPED HOOK ────────────────────────────────────────
// Failsafe default: no uid / no node / any error → Gilded Obsidian.
export function useEquippedBackdrop(uid) {
  const [equippedId, setEquippedId] = useState(DEFAULT_BACKDROP_ID);
  useEffect(() => {
    if (!uid) { setEquippedId(DEFAULT_BACKDROP_ID); return; }
    const r = ref(db, `profiles/${uid}/roomBackdrops/equipped`);
    const off = onValue(r, (snap) => {
      const v = snap.val();
      setEquippedId(typeof v === "string" && v ? v : DEFAULT_BACKDROP_ID);
    }, () => setEquippedId(DEFAULT_BACKDROP_ID));
    return () => off();
  }, [uid]);
  return equippedId;
}

// ── THE STAGE BAND — the one vertical rectangle where the magic
//    happens (caP'n, July 8). ──────────────────────────────────
// Confined to the desktop right chrome rail; portrait fills the
// full world zone. Geometry mirrors Plaza's RECORD_RESERVE math.
//
// CUT-OFF FIX (July 9): measure documentElement.clientWidth/Height,
// NOT window.innerWidth/Height. innerWidth includes the scrollbar,
// so the band's right edge hid underneath it whenever a scrollbar
// existed — that's the crop caP'n kept seeing.
const STAGE_BAND_MAX_W = 280;   // matches Plaza RECORD panel cap
const STAGE_BAND_VW    = 0.24;  // matches Plaza 24vw reserve
const STAGE_BAND_GAP   = 32;    // matches Plaza panel gap (right:12 + gutter)

function viewportSize() {
  const de = typeof document !== "undefined" ? document.documentElement : null;
  const vw = (de && de.clientWidth)  || window.innerWidth;
  const vh = (de && de.clientHeight) || window.innerHeight;
  return { vw, vh };
}

export function getStageBand() {
  if (typeof window === "undefined") {
    return { left: 0, top: 0, width: 300, height: 800, portrait: false, side: "right" };
  }
  const { vw, vh } = viewportSize();
  const portrait = vh > vw;
  if (portrait) {
    // No side bars in portrait — give the backdrop the full world slice.
    return { left: 0, top: 0, width: vw, height: vh, portrait: true, side: "full" };
  }
  const width = Math.round(Math.min(STAGE_BAND_MAX_W, vw * STAGE_BAND_VW) + STAGE_BAND_GAP);
  // Docked RIGHT — the map fills the space to its left.
  return { left: vw - width, top: 0, width, height: vh, portrait: false, side: "right" };
}

// Live hook — recomputes on resize/orientation, the same triggers
// the rooms use for their own tile math, so band and map stay synced.
export function useStageBand() {
  const [band, setBand] = useState(getStageBand);
  useEffect(() => {
    const update = () => setBand(getStageBand());
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);
  return band;
}

// Feather mask for the band's map-facing edge, so the backdrop melts
// into the void instead of cutting a hard rectangle. Direction keys
// off which side the band is docked; portrait/full gets no mask.
export function bandFeatherMask(side) {
  if (side === "right") {
    // transparent at the left (map side) → solid toward the right
    return "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.35) 8%, #000 28%, #000 100%)";
  }
  if (side === "left") {
    // solid at the left → transparent at the right (map side)
    return "linear-gradient(90deg, #000 0%, #000 72%, rgba(0,0,0,0.35) 92%, transparent 100%)";
  }
  return undefined; // full / portrait — no feather
}

// ── THE BACKDROP STAGE ────────────────────────────────────────
// Render FIRST inside a room's root; the map and HUD sit above it
// (zIndex 0). The equipped backdrop is CLIPPED to the stage band —
// a soft gradient mask feathers its inner edge so it melts into the
// void toward the map. Pass a `band` object to override geometry.
export default function RoomBackdrop({ uid, band }) {
  // caP'n 2026-09-23: "it needs to alllll fold into the menu, even the
  // backdrop." Folded rail → no band art. Nothing resizes; the art just goes.
  const railFold = useRail();
  if (railFold.active && railFold.collapsed) return null;
  return <RoomBackdropBand uid={uid} band={band} />;
}
function RoomBackdropBand({ uid, band }) {
  const liveBand = useStageBand();
  const b = band || liveBand;
  const featherMask = bandFeatherMask(b.side);
  return (
    <div aria-hidden="true" style={{
      position: "absolute",
      left: b.left, top: b.top, width: b.width, height: b.height,
      zIndex: 0, overflow: "hidden", pointerEvents: "none",
      WebkitMaskImage: featherMask, maskImage: featherMask,
    }}>
      <RoomBackdropInner uid={uid} />
    </div>
  );
}

// ── THE STAGE — one canonical room rectangle ──────────────────
// Canon geometry is Willie's Workshop: a 20×14 grid, tile = the
// largest whole pixel that fits both axes. EVERY room's play
// rectangle is meant to be exactly this box, centered.
export const STAGE_COLS = 20;
export const STAGE_ROWS = 14;

export function getStageRect() {
  if (typeof window === "undefined") {
    return { tile: 32, w: STAGE_COLS * 32, h: STAGE_ROWS * 32, left: 0, top: 0 };
  }
  const vw = window.innerWidth, vh = window.innerHeight;
  const tile = Math.max(1, Math.min(
    Math.floor(vw / STAGE_COLS),
    Math.floor(vh / STAGE_ROWS)
  ));
  const w = STAGE_COLS * tile, h = STAGE_ROWS * tile;
  return { tile, w, h, left: Math.round((vw - w) / 2), top: Math.round((vh - h) / 2) };
}

// Live hook — recomputes on resize/orientation, same triggers the
// rooms already use for their own tile math so the two stay in step.
export function useStageRect() {
  const [rect, setRect] = useState(getStageRect);
  useEffect(() => {
    const update = () => setRect(getStageRect());
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);
  return rect;
}

// ── THE BACKDROP UNDERLAY + STAGE HAIRLINE (overseer layers) ──
// (See July 2026 stacking-context lesson — bars-on-top is DEAD.)
//   <BackdropUnderlay/>  — BEFORE children, fixed, z 0, band-clipped.
//   <StageHairline/>     — AFTER children, the 1px gold outline.
export function BackdropUnderlay({ uid, band }) {
  const railFold = useRail();
  if (railFold.active && railFold.collapsed) return null;   // folds with the band
  return <BackdropUnderlayBand uid={uid} band={band} />;
}
function BackdropUnderlayBand({ uid, band }) {
  const liveBand = useStageBand();
  const b = band || liveBand;
  const featherMask = bandFeatherMask(b.side);
  return (
    <div aria-hidden="true" style={{
      position: "fixed",
      left: b.left, top: b.top, width: b.width, height: b.height,
      zIndex: 0, overflow: "hidden", pointerEvents: "none",
      WebkitMaskImage: featherMask, maskImage: featherMask,
    }}>
      <RoomBackdropInner uid={uid} />
    </div>
  );
}

// ── D10 — THE TRY-ON CHANNEL (2026-08-18) ────────────────────
// "Try the void on before you buy it." Any storefront can ask the
// live stage to WEAR a backdrop for a moment: previewBackdrop(id)
// dresses every mounted stage layer, previewBackdrop(null) takes
// it off. A window event, not props — the shelves live deep inside
// shop components while the stage layers are overseer-mounted, and
// threading App state through both for a hover would be the heavy
// version of two lines. Purely visual; Firebase is never touched.
const BACKDROP_PREVIEW_EVT = "ie-backdrop-preview";
export function previewBackdrop(id) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(BACKDROP_PREVIEW_EVT, { detail: id || null }));
}
// Hover is a desktop verb — on touch, mouseenter fires on tap and
// never leaves, sticking the preview. Only truly-hovering devices
// get the try-on; everyone still has the in-card miniatures.
export const CAN_HOVER = typeof window !== "undefined" && !!window.matchMedia?.("(hover: hover)")?.matches;

// Inner renderer shared by RoomBackdrop (absolute, in-room) and
// BackdropUnderlay (fixed, overseer) — one pattern source. Every
// instance listens on the try-on channel, so the preview appears
// on whichever stage layer the current room actually uses.
function RoomBackdropInner({ uid }) {
  const equippedId = useEquippedBackdrop(uid);
  const [previewId, setPreviewId] = useState(null);
  useEffect(() => {
    const on = (e) => setPreviewId(e.detail || null);
    window.addEventListener(BACKDROP_PREVIEW_EVT, on);
    return () => window.removeEventListener(BACKDROP_PREVIEW_EVT, on);
  }, []);
  const bd = getBackdrop(previewId || equippedId);
  return <>{bd.render()}</>;
}

export function StageHairline() {
  const rect = useStageRect();
  return (
    <div aria-hidden="true" style={{
      position: "fixed", left: rect.left, top: rect.top,
      width: rect.w, height: rect.h,
      border: `1px solid ${GM.inkGold}33`,
      pointerEvents: "none", zIndex: 30, boxSizing: "border-box",
    }} />
  );
}

// ── THE ATELIER ───────────────────────────────────────────────
// Browse · live-preview · purchase (DC) · equip. Opened from the
// RoomOverlay menu, so every wrapped room can reach it. v3 adds
// the FAMILY chip next to each rarity label.
export function BackdropAtelier({ profile, onClose }) {
  const uid = profile?.uid || null;

  const [crests, setCrests]     = useState(0);
  const [unlocked, setUnlocked] = useState({});
  const [equipped, setEquipped] = useState(DEFAULT_BACKDROP_ID);
  const [busy, setBusy]         = useState(null);   // backdrop id in flight
  const [notice, setNotice]     = useState(null);   // { text, color }
  // D10 — the try-on: while a card is hovered the stage behind wears
  // that backdrop, and the Atelier's own veil thins so you can SEE it.
  const [tryOnId, setTryOnId]   = useState(null);
  const tryOn = (id) => { setTryOnId(id); previewBackdrop(id); };
  useEffect(() => () => previewBackdrop(null), []); // never leave a fitting on

  // Live reads — ◕ Chocolate Coins balance + backdrop node
  useEffect(() => {
    if (!uid) return;
    const offA = onValue(ref(db, `profiles/${uid}/chocolateCoins`),
      (s) => setCrests(s.val() || 0));
    const offB = onValue(ref(db, `profiles/${uid}/roomBackdrops`),
      (s) => {
        const v = s.val() || {};
        setUnlocked(v.unlocked || {});
        setEquipped(typeof v.equipped === "string" && v.equipped ? v.equipped : DEFAULT_BACKDROP_ID);
      });
    return () => { offA(); offB(); };
  }, [uid]);

  const isOwned = (bd) => bd.cost === 0 || !!unlocked[bd.id];

  const handleBuy = async (bd) => {
    if (!uid || busy) return;
    setBusy(bd.id); setNotice(null);
    const res = await unlockBackdrop(uid, bd.id, bd.cost, "atelier_purchase");
    if (res.ok) {
      setNotice({ text: `◆ ${bd.name} unlocked.`, color: BACKDROP_RARITY[bd.rarity].color });
    } else if (res.reason === "insufficient-coins") {
      setNotice({ text: `Not enough Chocolate Coins for ${bd.name}.`, color: "#f43f5e" });
    } else {
      setNotice({ text: "The Atelier hiccuped — nothing was charged. Try again.", color: "#f43f5e" });
    }
    setBusy(null);
  };

  const handleEquip = async (bd) => {
    if (!uid || busy) return;
    setBusy(bd.id);
    await equipBackdrop(uid, bd.id);
    setBusy(null);
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 90,
      // D10 — the shop steps aside during a fitting: veil thins so the
      // stage behind (now wearing the hovered piece) shows through.
      background: tryOnId ? "rgba(3,2,8,0.45)" : "rgba(3,2,8,0.88)",
      backdropFilter: tryOnId ? "blur(1px)" : "blur(5px)",
      transition: "background 0.35s ease",
      overflowY: "auto", fontFamily: GM.font, color: GM.vellum,
    }}>
      {/* Header */}
      <div style={{
        position: "sticky", top: 0, zIndex: 5,
        background: "linear-gradient(180deg, rgba(5,3,12,0.97), rgba(5,3,12,0.88))",
        borderBottom: `1px solid ${GM.pageBorder}`,
        padding: "14px 18px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <div>
          <div style={{ fontSize: 9, letterSpacing: 4, fontWeight: 900, color: `${GM.inkGold}99` }}>
            ◆ CUSTOMIZE
          </div>
          <div style={{ fontSize: 19, fontWeight: 900, color: GM.inkGoldBright, letterSpacing: 1 }}>
            The Backdrop Atelier
          </div>
          <div style={{ fontSize: 10.5, fontStyle: "italic", color: GM.vellumDim, marginTop: 2 }}>
            Dress the void around every room. Equip anywhere, appear everywhere.
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{
            fontSize: 12, fontWeight: 900, color: GM.inkGoldBright,
            letterSpacing: 1, whiteSpace: "nowrap",
          }}>◆ {crests.toLocaleString()}</div>
          <button onClick={onClose} style={{
            padding: "7px 16px", borderRadius: 20, cursor: "pointer",
            border: `1px solid ${GM.inkGold}55`, background: "transparent",
            color: GM.inkGold, fontWeight: 900, fontSize: 11, letterSpacing: 2,
            fontFamily: GM.font,
          }}>✕ CLOSE</button>
        </div>
      </div>

      {/* Notice */}
      {notice && (
        <div style={{
          maxWidth: 860, margin: "12px auto 0", padding: "8px 16px",
          fontSize: 11.5, fontStyle: "italic", color: notice.color,
          textAlign: "center", letterSpacing: 1,
        }}>{notice.text}</div>
      )}

      {/* Catalog grid */}
      <div style={{
        maxWidth: 900, margin: "0 auto", padding: "18px 16px 70px",
        display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))",
        gap: 16,
      }}>
        {BACKDROPS.map(bd => {
          const rar = BACKDROP_RARITY[bd.rarity];
          const fam = BACKDROP_FAMILIES[bd.family] || null;
          const owned = isOwned(bd);
          const isEq = equipped === bd.id;
          return (
            <div key={bd.id}
              onMouseEnter={CAN_HOVER ? () => tryOn(bd.id) : undefined}
              onMouseLeave={CAN_HOVER ? () => tryOn(null) : undefined}
              style={{
              border: `1px solid ${isEq ? rar.color : GM.pageBorder}`,
              borderRadius: 12, overflow: "hidden",
              background: "rgba(10,8,18,0.85)",
              boxShadow: isEq ? `0 0 18px ${rar.color}33` : "0 4px 14px rgba(0,0,0,0.5)",
            }}>
              {/* Live preview — the backdrop rendering itself, in miniature */}
              <div style={{ position: "relative", height: 110, overflow: "hidden" }}>
                {bd.render()}
                <div style={{
                  position: "absolute", top: 8, left: 8,
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                  <span style={{
                    fontSize: 8, fontWeight: 900, letterSpacing: 2,
                    color: rar.color, textShadow: "0 1px 3px #000",
                  }}>{rar.label}</span>
                  {fam && (
                    <span style={{
                      fontSize: 7.5, fontWeight: 900, letterSpacing: 1.5,
                      color: fam.color, textShadow: "0 1px 3px #000",
                      border: `1px solid ${fam.color}55`, borderRadius: 8,
                      padding: "1px 6px", background: "rgba(0,0,0,0.35)",
                    }}>{fam.label}</span>
                  )}
                </div>
                {isEq && (
                  <div style={{
                    position: "absolute", top: 8, right: 8,
                    fontSize: 8, fontWeight: 900, letterSpacing: 2,
                    color: rar.color, textShadow: "0 1px 3px #000",
                  }}>◆ EQUIPPED</div>
                )}
              </div>

              <div style={{ padding: "10px 12px 12px" }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: GM.vellum }}>{bd.name}</div>
                <div style={{
                  fontSize: 10, fontStyle: "italic", color: GM.vellumDim,
                  marginTop: 3, minHeight: 26, lineHeight: 1.45,
                }}>{bd.lore}</div>

                <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
                  {!owned ? (
                    <button
                      onClick={() => handleBuy(bd)}
                      disabled={busy === bd.id || crests < bd.cost}
                      style={{
                        flex: 1, padding: "7px 0", borderRadius: 18,
                        cursor: crests < bd.cost ? "not-allowed" : "pointer",
                        border: `1px solid ${rar.color}66`,
                        background: "transparent",
                        color: crests < bd.cost ? GM.vellumFaint : rar.color,
                        fontFamily: GM.font, fontSize: 10.5, fontWeight: 900, letterSpacing: 1.5,
                        opacity: busy === bd.id ? 0.5 : 1,
                      }}
                    >
                      {busy === bd.id ? "…" : `UNLOCK — ◆ ${bd.cost.toLocaleString()}`}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleEquip(bd)}
                      disabled={isEq || busy === bd.id}
                      style={{
                        flex: 1, padding: "7px 0", borderRadius: 18,
                        cursor: isEq ? "default" : "pointer",
                        border: `1px solid ${isEq ? GM.pageBorder : GM.inkGold + "66"}`,
                        background: isEq ? "rgba(232,184,75,0.06)" : "transparent",
                        color: isEq ? GM.vellumDim : GM.inkGoldBright,
                        fontFamily: GM.font, fontSize: 10.5, fontWeight: 900, letterSpacing: 1.5,
                        opacity: busy === bd.id ? 0.5 : 1,
                      }}
                    >
                      {isEq ? "WEARING THE VOID" : "EQUIP"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
